import { useTranslation } from 'react-i18next';
import { Button, Input } from '../../../ui/index.js';
import type { AdvancedFilterState } from '../useRequestsFilters.js';
import { ManagerMultiPickerField } from './ManagerMultiPickerField.js';

export function RequestsAdvancedFilterPanel(props: {
  state: AdvancedFilterState;
  onChange: (next: AdvancedFilterState) => void;
  onClear: () => void;
  canPickManager: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder: string;
  searchMaxChars: number;
}) {
  const { t } = useTranslation('requests');
  const { state, onChange, onClear, canPickManager, search, onSearchChange } = props;
  const { searchPlaceholder, searchMaxChars } = props;
  const patch = (p: Partial<AdvancedFilterState>) => onChange({ ...state, ...p });
  return (
    <div
      id="requests-filter-panel"
      data-testid="requests-filter-panel"
      style={{
        padding: '12px 24px 14px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        background: 'var(--bg-1)',
        borderBottom: '1px solid var(--border-1)',
        flex: 'none',
      }}
    >
      <div style={{ gridColumn: '1 / -1' }}>
        <Input
          icon="search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={onSearchChange}
          maxLength={searchMaxChars}
        />
      </div>
      <Field label={t('list.filterPanel.dateRange')}>
        <DateInput
          value={state.dateFrom}
          onChange={(v) => patch({ dateFrom: v })}
          aria-label={t('list.filterPanel.dateFrom')}
          dataTestId="requests-filter-date-from"
        />
        <DateInput
          value={state.dateTo}
          onChange={(v) => patch({ dateTo: v })}
          aria-label={t('list.filterPanel.dateTo')}
          dataTestId="requests-filter-date-to"
        />
      </Field>
      <Field label={t('list.filterPanel.weightRange')}>
        <NumberInput
          value={state.weightFrom}
          onChange={(v) => patch({ weightFrom: v })}
          placeholder={t('list.filterPanel.rangeMin')}
          dataTestId="requests-filter-weight-from"
        />
        <NumberInput
          value={state.weightTo}
          onChange={(v) => patch({ weightTo: v })}
          placeholder={t('list.filterPanel.rangeMax')}
          dataTestId="requests-filter-weight-to"
        />
      </Field>
      <Field label={t('list.filterPanel.volumeRange')}>
        <NumberInput
          value={state.volumeFrom}
          onChange={(v) => patch({ volumeFrom: v })}
          placeholder={t('list.filterPanel.rangeMin')}
          dataTestId="requests-filter-volume-from"
        />
        <NumberInput
          value={state.volumeTo}
          onChange={(v) => patch({ volumeTo: v })}
          placeholder={t('list.filterPanel.rangeMax')}
          dataTestId="requests-filter-volume-to"
        />
      </Field>
      {canPickManager && (
        <ManagerMultiPickerField
          value={state.managerIds}
          onChange={(ids) => patch({ managerIds: ids })}
        />
      )}
      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button kind="ghost" size="sm" icon="x" onClick={onClear}>
          {t('list.filterPanel.clearAdvanced')}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--fg-3)',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{children}</div>
    </div>
  );
}

function DateInput(props: {
  value: string;
  onChange: (v: string) => void;
  'aria-label': string;
  dataTestId: string;
}) {
  return (
    <input
      type="date"
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      aria-label={props['aria-label']}
      data-testid={props.dataTestId}
      style={{
        flex: 1,
        height: 30,
        padding: '0 6px',
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
        color: 'var(--fg-1)',
        fontSize: 12,
      }}
    />
  );
}

function NumberInput(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  dataTestId: string;
}) {
  return (
    <input
      type="number"
      min={0}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      data-testid={props.dataTestId}
      style={{
        flex: 1,
        width: 80,
        height: 30,
        padding: '0 6px',
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
        color: 'var(--fg-1)',
        fontSize: 12,
      }}
    />
  );
}
